import React from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import './Staffing.css';

function Staffing() {
  usePageTitle('Staffing & Dedicated Teams');
  return (
    <div className="staffing">
      <section className="staffing-hero">
        <div className="staffing-container">
          <h1 className="staffing-hero-title">Scale Your Engineering Team Without Hiring Delays</h1>
          <p className="staffing-hero-subtitle">Get dedicated remote developers integrated with your team in 7 days</p>
          <div className="hero-cta">
            <Link to="/contact" className="btn-primary">Request Developers</Link>
            <Link to="/contact" className="btn-secondary">Schedule Hiring Call</Link>
          </div>
        </div>
      </section>

      <section className="staffing-provide">
        <div className="staffing-container">
          <h2 className="section-title">What We Provide</h2>
          <div className="provide-grid">
            <div className="provide-card">
              <h3>Remote Developers</h3>
              <p>Skilled engineers who work as an extension of your team</p>
            </div>
            <div className="provide-card">
              <h3>Dedicated Product Teams</h3>
              <p>Complete cross-functional teams for your projects</p>
            </div>
            <div className="provide-card">
              <h3>AI/ML Engineers</h3>
              <p>Specialists in machine learning and AI development</p>
            </div>
            <div className="provide-card">
              <h3>Full-Stack Developers</h3>
              <p>Frontend and backend development expertise</p>
            </div>
            <div className="provide-card">
              <h3>Mobile Developers</h3>
              <p>iOS, Android, React Native, Flutter experts</p>
            </div>
            <div className="provide-card">
              <h3>QA Engineers</h3>
              <p>Manual and automated testing specialists</p>
            </div>
            <div className="provide-card">
              <h3>DevOps Engineers</h3>
              <p>Cloud infrastructure and CI/CD experts</p>
            </div>
            <div className="provide-card">
              <h3>UI/UX Designers</h3>
              <p>Product design and user experience specialists</p>
            </div>
            <div className="provide-card">
              <h3>Data Engineers</h3>
              <p>Data pipeline and analytics infrastructure</p>
            </div>
          </div>
        </div>
      </section>

      <section className="staffing-models">
        <div className="staffing-container">
          <h2 className="section-title">Engagement Models</h2>
          <div className="models-grid">
            <div className="model-card">
              <h3>Staff Augmentation</h3>
              <p>Add individual developers to your existing team. They work under your management and follow your processes.</p>
              <ul>
                <li>Flexible scaling</li>
                <li>Your project management</li>
                <li>Direct communication</li>
                <li>Monthly billing</li>
              </ul>
            </div>
            <div className="model-card">
              <h3>Dedicated Team</h3>
              <p>Get a complete team including developers, QA, and project manager working exclusively on your project.</p>
              <ul>
                <li>Full team ownership</li>
                <li>Long-term partnership</li>
                <li>Scalable team size</li>
                <li>Dedicated resources</li>
              </ul>
            </div>
            <div className="model-card">
              <h3>Build-Operate-Transfer</h3>
              <p>We build your team, operate it, and eventually transfer it to your organization.</p>
              <ul>
                <li>Risk-free setup</li>
                <li>Knowledge transfer</li>
                <li>Smooth transition</li>
                <li>Full ownership</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="staffing-process">
        <div className="staffing-container">
          <h2 className="section-title">Our Hiring Process</h2>
          <div className="process-timeline">
            <div className="timeline-step">
              <div className="step-num">1</div>
              <h3>Requirement Discussion</h3>
              <p>We understand your technical needs, team culture, and project goals</p>
            </div>
            <div className="timeline-step">
              <div className="step-num">2</div>
              <h3>Shortlist Candidates</h3>
              <p>We present 3-5 pre-vetted candidates matching your requirements</p>
            </div>
            <div className="timeline-step">
              <div className="step-num">3</div>
              <h3>Client Interview</h3>
              <p>You interview candidates and select the best fit for your team</p>
            </div>
            <div className="timeline-step">
              <div className="step-num">4</div>
              <h3>2-Week Pilot</h3>
              <p>Risk-free trial period to ensure the right match</p>
            </div>
            <div className="timeline-step">
              <div className="step-num">5</div>
              <h3>Long-Term Engagement</h3>
              <p>Seamless integration and ongoing collaboration</p>
            </div>
          </div>
        </div>
      </section>

      <section className="staffing-roles">
        <div className="staffing-container">
          <h2 className="section-title">Available Roles</h2>
          <div className="roles-grid">
            <div className="role-card">
              <h3>🤖 AI Engineer</h3>
              <p>LLM integration, RAG systems, AI automation</p>
            </div>
            <div className="role-card">
              <h3>🧠 LLM Engineer</h3>
              <p>GPT, Claude, fine-tuning, prompt engineering</p>
            </div>
            <div className="role-card">
              <h3>🐍 Python Developer</h3>
              <p>Django, Flask, FastAPI, data processing</p>
            </div>
            <div className="role-card">
              <h3>⚛️ React Developer</h3>
              <p>React.js, Next.js, TypeScript, Redux</p>
            </div>
            <div className="role-card">
              <h3>🟢 Node Developer</h3>
              <p>Express, NestJS, GraphQL, microservices</p>
            </div>
            <div className="role-card">
              <h3>📱 Mobile App Developer</h3>
              <p>React Native, Flutter, iOS, Android</p>
            </div>
            <div className="role-card">
              <h3>🧪 QA Automation Engineer</h3>
              <p>Selenium, Cypress, Jest, test automation</p>
            </div>
            <div className="role-card">
              <h3>☁️ DevOps Engineer</h3>
              <p>AWS, Docker, Kubernetes, CI/CD</p>
            </div>
            <div className="role-card">
              <h3>🎨 UI/UX Designer</h3>
              <p>Figma, user research, prototyping</p>
            </div>
            <div className="role-card">
              <h3>📊 Data Analyst</h3>
              <p>SQL, Python, data visualization, BI tools</p>
            </div>
          </div>
        </div>
      </section>

      <section className="staffing-advantages">
        <div className="staffing-container">
          <h2 className="section-title">Why Choose Biltzentech for Staffing</h2>
          <div className="advantages-grid">
            <div className="advantage-card">
              <h3>⚡ Faster Than Recruitment</h3>
              <p>Get developers onboarded in 7 days vs 3-6 months for traditional hiring</p>
            </div>
            <div className="advantage-card">
              <h3>💰 Cost Effective</h3>
              <p>Save 60% compared to hiring in-house developers in Western markets</p>
            </div>
            <div className="advantage-card">
              <h3>🌍 Time Zone Overlap</h3>
              <p>Flexible working hours to match your team's schedule</p>
            </div>
            <div className="advantage-card">
              <h3>💬 English Communication</h3>
              <p>Fluent English-speaking developers with strong communication skills</p>
            </div>
            <div className="advantage-card">
              <h3>🔄 Agile Methodology</h3>
              <p>Experienced in Scrum, Kanban, and modern development practices</p>
            </div>
            <div className="advantage-card">
              <h3>📈 Easy Scaling</h3>
              <p>Scale your team up or down based on project needs</p>
            </div>
          </div>
        </div>
      </section>

      <section className="staffing-cta">
        <div className="staffing-container">
          <div className="cta-box">
            <h2>Build Your Remote Engineering Team in 7 Days</h2>
            <p>Let's discuss your hiring needs and find the perfect developers for your team</p>
            <Link to="/contact" className="btn-primary">Get Started Now</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Staffing;
